import { FC, Suspense, useEffect, useId } from 'react';
import { StudentSingleSkill } from '../components/StudentSingleSkill/StudentSingleSkill';
import { useAuth0 } from '@auth0/auth0-react';
import { selectUser } from '../redux/reducers/userSlice';
import { Roles } from '../models/User';
import { useAppSelector } from '../redux/hooks';
import { TeacherSingleSkill } from '../components/TeacherSingleSkill/TeacherSingleSkill';
import { useNavigate, useParams } from 'react-router-dom';
import { useCondition } from '../experiments/ExperimentsProvider';
import { Skill213Replacement, SKILL_213_CODE, TEAME_183_FLAG_ID, TEAME_183_TREATMENT } from '../experiments/TEAME-183';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Toolbar } from 'primereact/toolbar';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { getPresignedURLsForSkill, getSkillInfoByCode } from '../graphQLOperations/Skills';
import { SkillsTeacherSingleSkillByCodeQuery } from '../graphQLOperations/__generated__/SkillsTeacherSingleSkillByCodeQuery.graphql';
import { SkillsPresignedURLsQuery } from '../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';

export const SingleSkill: FC = () => {
  const { isAuthenticated } = useAuth0();
  const userRedux = useAppSelector(selectUser);
  const studentRole = [Roles.STUDENT];
  const isStudent = userRedux?.roles.some(role => studentRole?.includes(role?.name));
  const { skillCode } = useParams();
  const visitKey = useId();
  const skill213Condition = useCondition(TEAME_183_FLAG_ID);
  const showSkill213Replacement = skillCode === SKILL_213_CODE && skill213Condition === TEAME_183_TREATMENT;

  const skillData = useLazyLoadQuery<SkillsTeacherSingleSkillByCodeQuery>(getSkillInfoByCode, {
    skillCode: skillCode || '',
    skip: !!isStudent,
  });

  let breadcrumbItems;
  let home;
  if (!isStudent && skillData) {
    breadcrumbItems = [
      { label: 'Plan', command: () => navigate('/planning') },
      { label: 'Skills', command: () => navigate('/skills') },
      { label: skillData.siteSpecificSkillInfoBySkillCode?.name || '' },
    ];

    const navigate = useNavigate();
    home = {
      icon: 'nc_icon-nc_home icon-sm',
      command: () => navigate('/'),
      template: () => (
        <span onClick={() => navigate('/')} className="p-menuitem-link cursor-pointer">
          <span className="nc_icon-nc_home icon-sm p-menuitem-icon" />
        </span>
      ),
    };
  }

  const [skillsPresignedURLsQueryReference, loadSkillsPresignedURLsQuery] =
    useQueryLoader<SkillsPresignedURLsQuery>(getPresignedURLsForSkill);

  useEffect(() => {
    loadSkillsPresignedURLsQuery({
      skillCode: skillCode || '',
      filter: { storedObjectType: isStudent ? 'WORKED_EXAMPLE_VIDEO' : 'ALL' },
    });
  }, [skillCode, isStudent, loadSkillsPresignedURLsQuery]);

  return (
    <>
      {isAuthenticated && isStudent ? (
        <Suspense>
          {showSkill213Replacement ? <Skill213Replacement /> : <StudentSingleSkill forceRefetch={visitKey} />}
        </Suspense>
      ) : (
        isAuthenticated &&
        skillData && (
          <div className="grid nc_content-frame nc_page-inner">
            <Toolbar
              className="col-10 col-offset-1 nc_page-header"
              center={<h1>{skillData.siteSpecificSkillInfoBySkillCode?.name}</h1>}
            ></Toolbar>
            <BreadCrumb className="col-10 col-offset-1 nc_breadcrumb" model={breadcrumbItems} home={home} />
            <div className="col-10 col-offset-1 nc_page-header">
              {skillsPresignedURLsQueryReference && (
                <TeacherSingleSkill
                  skillCode={skillCode}
                  entryPoint="single_skill"
                  skillsPresignedURLsQueryReference={
                    skillsPresignedURLsQueryReference as PreloadedQuery<
                      SkillsPresignedURLsQuery,
                      Record<string, unknown>
                    >
                  }
                />
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};
